import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent,
  type ReactElement,
  type ReactNode,
} from 'react';
import { SearchIcon, PlusIcon } from '@/components/catalog/icons';
import {
  NoteDeleteDialog,
  NoteIcon,
  NoteRow,
  NotesSkeleton,
} from '@/components/notes/NotesPanel';
import {
  MAX_BODY,
  deleteRefusalMessage,
  editRefusalMessage,
  writeMessage,
} from '@/components/notes/note-rules';
import {
  Button,
  Checkbox,
  CollapsibleSection,
  EmptyState,
  Input,
  MultiCombobox,
  Textarea,
} from '@/components/ui';
import { Notice } from '@/components/ui/Notice';
import { TextLink } from '@/components/ui/TextLink';
import { ApiError, api } from '@/lib/api';
import { useRememberedState } from '@/lib/filter-memory/use-filter-memory';
import { pluralise } from '@/lib/format';
import { denialReason } from '@/lib/permissions';
import { useAuth } from '@/lib/use-auth';
import type { Note, NoteListParams, TaskTargetType } from '@/lib/types';

/*
 * The company page's "Notes" section (owner, 2026-10-16: "add a notes section
 * also similar to tasks, and the layout of notes can be very easy to use with
 * all features").
 *
 * Every note on the account — on the company row itself AND on its deals,
 * quotes, invoices and deliverables — newest first, with a composer on top.
 * The `ActivityDrawer` behind the header's "Tasks & notes" button is unchanged
 * and still shows the notes on the company row alone; this section is the
 * wider read. The row, the byline, the edit/delete rules, the refusal
 * sentences and the delete dialog are the drawer's own (`NotesPanel` /
 * `note-rules`), so the two surfaces cannot word a note differently.
 *
 * ## The read
 *
 * `GET /notes?relatedCompanyId=&search=&system=false`. The query is STRICT, so
 * an older server answers 422 naming the unknown key; on exactly that refusal
 * this falls back ONCE (per fetch) to `companyId=` — the company row only — and
 * applies the search and the system filter in the browser. Nothing is cached
 * across fetches, so the wider read starts working the moment the server ships.
 *
 * `perPage` is capped at 100 server-side, so this pages through up to
 * `MAX_PAGES`. Past that the list is truncated and the section SAYS SO as a fact
 * about the data; the filter counts are then counts of what was read.
 *
 * ## The filters
 *
 * Search is server-side (debounced 250 ms). "System notes" off — the default —
 * sends `system=false`, so only notes people wrote are listed; on, the key is
 * omitted and both kinds come back. The Company · Deals · Quotes · Invoices
 * multi-select (2026-10-28; was a one-of toggle) narrows the loaded list in the
 * browser by set membership; an empty selection is every record, and a note on
 * a deliverable shows only then.
 *
 * ## Writes
 *
 * The composer always writes onto the COMPANY (`companyId`). Edit and delete
 * follow the drawer's rules exactly — author-only edit, author-or-audit delete,
 * system notes immutable — because they are the same `NoteRow`. Every accepted
 * write calls `onChanged`, which the page turns into ONE reload token shared
 * with the drawer and the Tasks section.
 */

const PER_PAGE = 100;
const MAX_PAGES = 10;
const SEARCH_DEBOUNCE_MS = 250;

type NoteFilter = 'company' | 'deal' | 'quote' | 'invoice';

const FILTERS: readonly NoteFilter[] = ['company', 'deal', 'quote', 'invoice'] as const;

/**
 * The remembered record filter: a set of record types, `[]` = every record
 * (multi-value since 2026-10-28). The pre-2026-10-28 single value still
 * restores: `'all'` = `[]`, a type = that type alone.
 */
function parseNoteFilters(raw: unknown): NoteFilter[] | undefined {
  if (raw === 'all') return [];
  const values = typeof raw === 'string' ? [raw] : Array.isArray(raw) ? raw : null;
  if (values === null) return undefined;
  return FILTERS.filter((filter) => values.includes(filter));
}

const FILTER_LABEL: Record<NoteFilter, string> = {
  company: 'Company',
  deal: 'Deals',
  quote: 'Quotes',
  invoice: 'Invoices',
};

const TARGET_NOUN: Record<TaskTargetType, string> = {
  deal: 'Deal',
  quote: 'Quote',
  invoice: 'Invoice',
  deliverable: 'Deliverable',
  company: 'Company',
};

/** Where a note's record opens. A deliverable has no detail page. */
const TARGET_ROUTE: Record<TaskTargetType, string | null> = {
  deal: '/deals',
  quote: '/quotes',
  invoice: '/invoices',
  deliverable: null,
  company: null,
};

/* --- the read ------------------------------------------------------------ */

const NEW_KEYS = ['relatedCompanyId', 'search', 'system'] as const;

/** True for an older server's strict-query refusal of one of the new keys. */
function refusesNewFilter(cause: unknown): boolean {
  if (!(cause instanceof ApiError) || cause.status !== 422) return false;
  const mentions = (text: unknown) => NEW_KEYS.some((key) => String(text).includes(key));
  const details = cause.details ?? [];
  return (
    details.some(
      (detail) =>
        ('key' in detail && NEW_KEYS.some((key) => detail.key === key)) ||
        mentions(detail.path) ||
        mentions(detail.message),
    ) || mentions(cause.message)
  );
}

interface NoteRead {
  notes: Note[];
  /** The server's full count for the query, so a truncated read can say so. */
  total: number;
  /** `'fallback'` = an older server: company row only, filters applied here. */
  mode: 'server' | 'fallback';
}

async function readAllNotes(
  companyId: number,
  search: string,
  includeSystem: boolean,
  signal: AbortSignal,
): Promise<NoteRead> {
  let filter: NoteListParams = {
    relatedCompanyId: companyId,
    ...(search ? { search } : {}),
    ...(includeSystem ? {} : { system: false }),
  };
  let mode: NoteRead['mode'] = 'server';

  let first;
  try {
    first = await api.listNotes({ ...filter, perPage: PER_PAGE, page: 1 }, signal);
  } catch (cause) {
    if (!refusesNewFilter(cause)) throw cause;
    // Older server: the company row only, filtered below. Once, never cached.
    filter = { companyId };
    mode = 'fallback';
    first = await api.listNotes({ ...filter, perPage: PER_PAGE, page: 1 }, signal);
  }

  const notes: Note[] = [...first.data];
  const lastPage = Math.min(first.meta.totalPages, MAX_PAGES);
  for (let page = 2; page <= lastPage; page += 1) {
    const next = await api.listNotes({ ...filter, perPage: PER_PAGE, page }, signal);
    notes.push(...next.data);
    if (next.data.length === 0) break;
  }

  // A row can shift pages between requests; the same note must not render twice.
  const seen = new Set<number>();
  const unique = notes.filter((note) => (seen.has(note.id) ? false : (seen.add(note.id), true)));
  return { notes: unique, total: first.meta.total, mode };
}

function loadMessage(cause: unknown): string {
  if (cause instanceof ApiError) {
    if (cause.status === 403) return 'You do not have permission to read these notes.';
    if (cause.status === 404) return 'This company no longer exists.';
    return cause.message;
  }
  return 'Could not load the notes. Check your connection and try again.';
}

function filterOf(note: Note): NoteFilter | null {
  const type = note.target.type;
  return type === 'deliverable' ? null : type;
}

/** The record a note sits on, when it is not the company itself. */
function contextOf(note: Note): ReactNode | undefined {
  const target = note.target;
  if (target.type === 'company') return undefined;
  const noun = TARGET_NOUN[target.type];
  const name = target.name?.trim();
  const label = name ? `${noun} · ${name}` : `${noun} #${target.id}`;
  const route = TARGET_ROUTE[target.type];
  return route ? (
    <TextLink tone="muted" to={`${route}/${target.id}`}>
      {label}
    </TextLink>
  ) : (
    label
  );
}

/* --- the section --------------------------------------------------------- */

interface NoteSlot {
  /** `${companyId}:${search}:${system}:${stamp}` — what this answer describes. */
  key: string;
  companyId: number;
  notes: Note[] | null;
  total: number | null;
  mode: NoteRead['mode'];
  error: string | null;
}

export interface CompanyNotesSectionProps {
  /** The shell's own id, from the page's `sectionShell`. */
  id: string;
  collapsed: boolean;
  onToggle: () => void;
  companyId: number;
  /** The page's shared reload token — bumped by this section, the Tasks section and the drawer. */
  reloadNonce: number;
  /** Fires on every write the server ACCEPTED. Never on a refusal. */
  onChanged: () => void;
}

export function CompanyNotesSection({
  id,
  collapsed,
  onToggle,
  companyId,
  reloadNonce,
  onChanged,
}: CompanyNotesSectionProps): ReactElement {
  const { ability, user } = useAuth();
  const canCreate = ability.canCreate('notes');
  const canUpdate = ability.canUpdate('notes');
  const canDelete = ability.canDelete('notes');
  const createDenial = canCreate ? null : denialReason(ability, 'create', 'notes');
  const viewerId = user?.id ?? null;

  // The filter, the search and the system toggle are remembered on the account
  // (owner, 2026-10-16); drafts, edits and dialogs are not.
  const [recordFilters, setRecordFilters] = useRememberedState<NoteFilter[]>(
    'company-notes-filter',
    [],
    parseNoteFilters,
  );
  const [searchInput, setSearchInput] = useRememberedState('company-notes-search', '');
  const [search, setSearch] = useState(() => searchInput.trim());
  const [includeSystem, setIncludeSystem] = useRememberedState('company-notes-system', false);
  const [retry, setRetry] = useState(0);
  const [slot, setSlot] = useState<NoteSlot | null>(null);

  const [draft, setDraft] = useState('');
  const [adding, setAdding] = useState(false);
  const [addError, setAddError] = useState<string | null>(null);
  const composerRef = useRef<HTMLTextAreaElement>(null);
  const [focusRequest, setFocusRequest] = useState(0);

  const [editing, setEditing] = useState<{ id: number; draft: string } | null>(null);
  const [busyId, setBusyId] = useState<number | null>(null);
  const [rowError, setRowError] = useState<{ id: number; message: string } | null>(null);

  const [pendingDelete, setPendingDelete] = useState<Note | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  // Server-side search, debounced.
  useEffect(() => {
    const handle = window.setTimeout(() => setSearch(searchInput.trim()), SEARCH_DEBOUNCE_MS);
    return () => window.clearTimeout(handle);
  }, [searchInput]);

  const stampKey = `${companyId}:${search}:${includeSystem}:${reloadNonce}:${retry}`;

  useEffect(() => {
    const controller = new AbortController();
    const key = `${companyId}:${search}:${includeSystem}:${reloadNonce}:${retry}`;
    readAllNotes(companyId, search, includeSystem, controller.signal)
      .then((read) => {
        if (controller.signal.aborted) return;
        setSlot({ key, companyId, notes: read.notes, total: read.total, mode: read.mode, error: null });
      })
      .catch((cause: unknown) => {
        if (controller.signal.aborted) return;
        setSlot({ key, companyId, notes: null, total: null, mode: 'server', error: loadMessage(cause) });
      });
    return () => controller.abort();
  }, [companyId, search, includeSystem, reloadNonce, retry]);

  // "+ Add note": open the section if it is folded, then focus the composer.
  useEffect(() => {
    if (focusRequest === 0 || collapsed) return;
    composerRef.current?.focus();
  }, [focusRequest, collapsed]);

  // Another company's answer never renders here; an older answer for THIS
  // company stays on screen, dimmed, while its refetch is in flight.
  const current = slot?.companyId === companyId ? slot : null;
  const loading = current === null;
  const refetching = current !== null && current.key !== stampKey;
  const error = current?.error ?? null;

  /*
   * What the filters apply to. Against a current server the read is already
   * narrowed; against an older one (`'fallback'`) the search and the system
   * filter are applied here, over the company row's notes.
   */
  const visible = useMemo(() => {
    const notes = current?.notes ?? null;
    if (notes === null) return null;
    if (current?.mode !== 'fallback') return notes;
    const needle = search.toLowerCase();
    return notes.filter(
      (note) =>
        (includeSystem || !note.system) &&
        (needle === '' || note.body.toLowerCase().includes(needle)),
    );
  }, [current, search, includeSystem]);

  const counts = useMemo(() => {
    const out: Record<NoteFilter, number> = { company: 0, deal: 0, quote: 0, invoice: 0 };
    for (const note of visible ?? []) {
      const kind = filterOf(note);
      if (kind !== null) out[kind] += 1;
    }
    return out;
  }, [visible]);

  // Set membership; an empty set is every record (deliverable notes included).
  const rows = useMemo(() => {
    if (recordFilters.length === 0) return visible ?? [];
    const picked = new Set<NoteFilter>(recordFilters);
    return (visible ?? []).filter((note) => {
      const kind = filterOf(note);
      return kind !== null && picked.has(kind);
    });
  }, [visible, recordFilters]);

  const shown = current?.notes?.length ?? 0;
  const total = current?.total ?? null;
  const truncated = (current?.notes ?? null) !== null && total !== null && total > shown;
  const narrowed = search !== '' || recordFilters.length > 0;

  function patch(change: (notes: Note[]) => Note[]) {
    setSlot((prev) =>
      prev && prev.companyId === companyId && prev.notes !== null
        ? { ...prev, notes: change(prev.notes) }
        : prev,
    );
  }

  function addNote() {
    const body = draft.trim();
    if (body === '' || adding || !canCreate) return;
    setAdding(true);
    setAddError(null);
    api
      .createNote({ body, companyId })
      .then((response) => {
        // Newest first, so a new note goes on top; the shared reload re-reads.
        patch((notes) => [response.data, ...notes]);
        setDraft('');
        onChanged();
      })
      .catch((cause: unknown) => {
        setAddError(writeMessage(cause, 'Could not save this note. Try again.'));
      })
      .finally(() => setAdding(false));
  }

  function onComposerKeyDown(event: KeyboardEvent<HTMLTextAreaElement>) {
    if (event.key === 'Enter' && (event.ctrlKey || event.metaKey)) {
      event.preventDefault();
      addNote();
    }
  }

  function saveEdit(note: Note) {
    const body = editing?.draft.trim() ?? '';
    if (body === '' || busyId !== null) return;
    setBusyId(note.id);
    setRowError(null);
    api
      .updateNote(note.id, { body })
      .then((response) => {
        patch((notes) => notes.map((item) => (item.id === note.id ? response.data : item)));
        setEditing(null);
        onChanged();
      })
      .catch((cause: unknown) => {
        setRowError({ id: note.id, message: editRefusalMessage(cause) });
      })
      .finally(() => setBusyId(null));
  }

  function confirmDelete() {
    const note = pendingDelete;
    if (!note || deleting) return;
    setDeleting(true);
    setDeleteError(null);
    api
      .deleteNote(note.id)
      .then(() => {
        patch((notes) => notes.filter((item) => item.id !== note.id));
        setPendingDelete(null);
        onChanged();
      })
      .catch((cause: unknown) => {
        setDeleteError(deleteRefusalMessage(cause));
      })
      .finally(() => setDeleting(false));
  }

  // The folded summary counts the whole list in force (search and the system
  // toggle included, the record filter not); past the page cap, the server's total.
  const summaryCount = visible === null ? null : truncated && total !== null ? total : visible.length;
  const summary = summaryCount === null ? undefined : pluralise(summaryCount, 'note');

  const addButton = canCreate ? (
    <Button
      size="sm"
      leadingIcon={<PlusIcon />}
      onClick={() => {
        if (collapsed) onToggle();
        setFocusRequest((value) => value + 1);
      }}
    >
      Add note
    </Button>
  ) : undefined;

  return (
    <CollapsibleSection
      id={id}
      title="Notes"
      {...(summary ? { summary } : {})}
      actions={addButton}
      collapsed={collapsed}
      onToggle={onToggle}
    >
      <div className="flex flex-col gap-3 px-4 py-4">
        {/* Composer — always writes onto the company. */}
        <div className="min-w-0">
          <Textarea
            ref={composerRef}
            value={draft}
            onChange={(event) => setDraft(event.target.value)}
            onKeyDown={onComposerKeyDown}
            maxLength={MAX_BODY}
            rows={3}
            disabled={adding || !canCreate}
            aria-label="New note on this company"
          />
          <div className="mt-2 flex min-w-0 flex-wrap items-center justify-between gap-2">
            <span className="min-w-0 text-2xs text-fg-subtle">
              {createDenial
                ? createDenial
                : draft.length > MAX_BODY - 500
                  ? `${(MAX_BODY - draft.length).toLocaleString()} characters left`
                  : null}
            </span>
            <Button
              size="sm"
              variant="primary"
              onClick={addNote}
              loading={adding}
              disabled={!canCreate || draft.trim() === ''}
            >
              Add note
            </Button>
          </div>
          {addError && (
            <div className="mt-2">
              <Notice tone="danger" role="alert">
                <span className="min-w-0 [overflow-wrap:anywhere]">{addError}</span>
              </Notice>
            </div>
          )}
        </div>

        {/* Toolbar: search, record filter, system notes. */}
        <div className="flex min-w-0 flex-wrap items-center gap-x-4 gap-y-2">
          <div className="w-full min-w-0 sm:w-64">
            <Input
              type="search"
              value={searchInput}
              onChange={(event) => setSearchInput(event.target.value)}
              leadingIcon={<SearchIcon />}
              aria-label="Search notes"
            />
          </div>
          {/* SUPERSEDED 2026-10-28: a one-of ViewToggle (All / Company / Deals /
              Quotes / Invoices). Multi-value now; empty = every record. The
              per-type count rides as each option's hint. */}
          <div className="w-full min-w-0 sm:w-56">
            <MultiCombobox
              values={recordFilters}
              onChange={(values) => setRecordFilters(FILTERS.filter((filter) => values.includes(filter)))}
              options={FILTERS.map((filter) => ({
                value: filter,
                label: FILTER_LABEL[filter],
                ...(visible !== null ? { hint: String(counts[filter]) } : {}),
              }))}
              placeholder={visible !== null ? `All records (${visible.length})` : 'All records'}
              searchPlaceholder="Search records…"
              summaryNoun={{ singular: 'record type', plural: 'record types' }}
              maxVisibleChips={2}
            />
          </div>
          <Checkbox
            label="System notes"
            checked={includeSystem}
            onChange={(event) => setIncludeSystem(event.target.checked)}
          />
        </div>

        {loading ? (
          <div className="rounded-md border border-border">
            <NotesSkeleton />
          </div>
        ) : error !== null ? (
          <Notice
            tone="danger"
            role="alert"
            title="Couldn’t load notes."
            action={
              <Button size="sm" onClick={() => setRetry((value) => value + 1)}>
                Try again
              </Button>
            }
          >
            <span className="min-w-0 [overflow-wrap:anywhere]">{error}</span>
          </Notice>
        ) : rows.length === 0 ? (
          <EmptyState icon={<NoteIcon />} title={narrowed ? 'No notes match' : 'No notes yet'} />
        ) : (
          <div
            aria-busy={refetching || undefined}
            className={
              refetching
                ? 'rounded-md border border-border opacity-60 transition-opacity duration-150 ease-out motion-reduce:transition-none'
                : 'rounded-md border border-border'
            }
          >
            <ul className="min-w-0">
              {rows.map((note) => (
                <NoteRow
                  key={note.id}
                  note={note}
                  context={contextOf(note)}
                  viewerId={viewerId}
                  canUpdate={canUpdate}
                  canDelete={canDelete}
                  busy={busyId === note.id}
                  error={rowError?.id === note.id ? rowError.message : null}
                  editing={editing?.id === note.id}
                  editDraft={editing?.id === note.id ? editing.draft : note.body}
                  onEditDraftChange={(value) =>
                    setEditing((previous) => (previous ? { ...previous, draft: value } : previous))
                  }
                  onStartEdit={(subject) => {
                    setEditing({ id: subject.id, draft: subject.body });
                    setRowError(null);
                  }}
                  onCancelEdit={() => {
                    setEditing(null);
                    setRowError(null);
                  }}
                  onSaveEdit={saveEdit}
                  onRequestDelete={(subject) => {
                    setPendingDelete(subject);
                    setDeleteError(null);
                  }}
                />
              ))}
            </ul>
          </div>
        )}

        {/* A fact about the data, not advice: past the page cap the counts are
            counting what was read, not everything there is. */}
        {truncated && total !== null && (
          <p className="m-0 text-xs text-fg-muted">
            Showing the newest {shown.toLocaleString()} of {total.toLocaleString()} notes.
          </p>
        )}
      </div>

      <NoteDeleteDialog
        note={pendingDelete}
        onClose={() => {
          setPendingDelete(null);
          setDeleteError(null);
        }}
        onConfirm={confirmDelete}
        deleting={deleting}
        error={deleteError}
      />
    </CollapsibleSection>
  );
}
